import { useEffect, useState, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import api, { 
  getUsers, 
  getDepartments, 
  getRoles, 
  getLocations,
  createUser,
  updateUser,
  createDepartment, 
  createRole,
  deleteUser,
  deleteDepartment,
  deleteRole,
  toggleUserActive,
  regenerateUserUrl,
  getUserActivity,
  exportUsers,
  getImportTemplate,
  importUsers,
  getUserStats,
  uploadUserAvatar,
  deleteUserAvatar,
  getUserAvatarUrl,
  getAvatarDisplayUrl,
  getBulkUserInsights,
  createLoginUser,
  updateLoginUser,
  deleteLoginUser,
  setUserPassword,
  removeUserPassword,
  // User Archive/Restore/Delete
  softDeleteUsers,
  getDeletedUsers,
  getDeletedUserArchivedData,
  getDeletedUsersArchivedStats,
  restoreDeletedUsers,
  permanentDeleteUsers
} from '@/lib/api';
import { UserInsightsPopup, UserInsightsBadge } from '@/components/UserInsightsPopup';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Copy, Users as UsersIcon, Building2, UserCog, Loader2,
  MoreHorizontal, Eye, Edit, Power, History, RefreshCw, ArrowUpDown,
  Search, Filter, MapPin, Briefcase, ChevronUp, ChevronDown, Check, ChevronsUpDown,
  Download, Upload, FileSpreadsheet, CheckCircle, XCircle, Phone, Star, BarChart3,
  Camera, X, ImagePlus, KeyRound, Shield, Lock, LockOpen, Key, Crown,
  Archive, RotateCcw, AlertTriangle, Database, UserCheck, UserX, UserPlus, ArrowRight, Sparkles
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import CumulativeLearningReport from '@/components/CumulativeLearningReport';
import WorkspaceAccessDialog from '@/components/WorkspaceAccessDialog';
import { ImageCropper } from '@/components/ImageCropper';

export default function AdminUsers() {
  // Handle both legacy admin and SaaS tenant contexts
  const outletContext = useOutletContext() || {};
  const adminUrl = outletContext.adminUrl;
  const tenant = outletContext.tenant;
  const currentUser = outletContext.user;
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const importFileRef = useRef(null);
  const avatarFileRef = useRef(null);
  // Form states
  const [userForm, setUserForm] = useState({ 
    name: '', email: '', phone_number: '', extension: '', employee_id: '',
    phone_numbers: ['', ''],  // Additional phone numbers (2 more for total of 3)
    extensions: ['', ''],     // Additional extensions (2 more for total of 3)
    role_id: '', department_id: '', designation: '', location_id: '',
    reports_to: '', hierarchy_level: ''
  });
  const [showAdditionalPhones, setShowAdditionalPhones] = useState(false);
  const [showAdditionalExtensions, setShowAdditionalExtensions] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImageSrc, setCropperImageSrc] = useState(null);
  const [deptForm, setDeptForm] = useState({ name: '', description: '' });
  const [roleForm, setRoleForm] = useState({ name: '', description: '' });
  const [hierarchyLevels, setHierarchyLevels] = useState([]);
  
  // Dialog states
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [deptDialogOpen, setDeptDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [viewingUserStats, setViewingUserStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [activityLogUser, setActivityLogUser] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  
  // Login User Dialog states (for creating users with login credentials)
  const [loginUserDialogOpen, setLoginUserDialogOpen] = useState(false);
  const [loginUserForm, setLoginUserForm] = useState({
    name: '', email: '', password: '', role_id: 'quality_auditor',
    phone_number: '', extension: '', department: '',
    feature_access: {
      billing: false,
      master_delete: false,
      audio_settings: false,
      onedrive: false
    }
  });
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [isCreatingLoginUser, setIsCreatingLoginUser] = useState(false);
  
  // Set Password Dialog states
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ password: '', auto_generate: false });
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState(null);

  // Cumulative Coaching Report dialog
  const [coachingReportOpen, setCoachingReportOpen] = useState(false);
  const [coachingReportUser, setCoachingReportUser] = useState(null);

  // Workspace Access dialog (Feb 2026 — multi-tenant access toggle)
  const [workspaceAccessOpen, setWorkspaceAccessOpen] = useState(false);
  const [workspaceAccessUser, setWorkspaceAccessUser] = useState(null);
  
  // Insights states
  const [insightsUser, setInsightsUser] = useState(null);
  const [bulkInsights, setBulkInsights] = useState({});
  
  // Deleted Users states
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [deletedUsersStats, setDeletedUsersStats] = useState(null);
  const [isLoadingDeleted, setIsLoadingDeleted] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [usersToDelete, setUsersToDelete] = useState([]);
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedDeletedUsers, setSelectedDeletedUsers] = useState([]);
  const [viewingDeletedUser, setViewingDeletedUser] = useState(null);
  const [deletedUserArchivedData, setDeletedUserArchivedData] = useState(null);
  
  // Reports To Combobox states
  const [reportsToOpen, setReportsToOpen] = useState(false);
  const [reportsToSearch, setReportsToSearch] = useState('');
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Sort states
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // =============== ASSIGNMENTS TAB STATE ===============
  const [auditors, setAuditors] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsTab, setAssignmentsTab] = useState('all');
  const [assignmentSearchQuery, setAssignmentSearchQuery] = useState('');
  const [filterAuditor, setFilterAuditor] = useState('all');
  const [selectedUsersForAssignment, setSelectedUsersForAssignment] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [selectedAuditorForAssign, setSelectedAuditorForAssign] = useState('');
  const [assigningLoading, setAssigningLoading] = useState(false);
  const [assignmentStats, setAssignmentStats] = useState({ total: 0, assigned: 0, unassigned: 0 });

  const fetchData = async () => {
    try {
      const [usersRes, deptsRes, rolesRes, levelsRes, locationsRes] = await Promise.all([
        getUsers(),
        getDepartments(),
        getRoles(),
        api.get('/hierarchy-levels').catch(() => ({ data: [] })),
        getLocations().catch(() => ({ data: { locations: [] } }))
      ]);
      setUsers(usersRes.data);
      setFilteredUsers(usersRes.data);
      setDepartments(deptsRes.data);
      setRoles(rolesRes.data);
      setHierarchyLevels(levelsRes.data || []);
      setLocations(locationsRes.data?.locations || []);
      
      // Load bulk insights in background
      fetchBulkInsights();
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchBulkInsights = async () => {
    try {
      const response = await getBulkUserInsights();
      setBulkInsights(response.data?.insights || {});
    } catch (error) {
      console.log('Could not load bulk insights:', error);
    }
  };

  // =============== ASSIGNMENTS TAB FUNCTIONS ===============
  const fetchAssignmentsData = async () => {
    setAssignmentsLoading(true);
    try {
      // Fetch auditors (users with auditor role)
      const auditorUsers = users.filter(u => 
        u.role_id === 'auditor' || u.role_id === 'quality_auditor' || 
        u.role === 'auditor' || u.role === 'quality_auditor'
      );
      setAuditors(auditorUsers);
      
      // Fetch existing assignments
      const assignmentsRes = await api.get('/qa-tasks/assignments');
      const allAssignments = assignmentsRes.data.assignments || [];
      setAssignments(allAssignments);
      
      // Build user-to-auditor mapping
      const userAssignmentMap = new Map();
      allAssignments.forEach(assignment => {
        const auditorId = assignment.auditor_id;
        const auditor = users.find(u => u.id === auditorId);
        const auditorName = auditor?.name || assignment.auditor_name || 'Unknown Auditor';
        (assignment.assigned_user_ids || []).forEach(userId => {
          userAssignmentMap.set(userId, {
            auditorId,
            auditorName,
            assignmentId: assignment.id
          });
        });
      });
      
      // Calculate stats
      const enrichedUsers = users.map(u => {
        const assignment = userAssignmentMap.get(u.id);
        return { ...u, isAssigned: !!assignment, assignedTo: assignment?.auditorName, assignedToId: assignment?.auditorId };
      });
      
      const assignedCount = enrichedUsers.filter(u => u.isAssigned).length;
      setAssignmentStats({
        total: users.length,
        assigned: assignedCount,
        unassigned: users.length - assignedCount
      });
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const getUserAssignmentInfo = (userId) => {
    for (const assignment of assignments) {
      if (assignment.assigned_user_ids?.includes(userId)) {
        const auditor = users.find(u => u.id === assignment.auditor_id);
        return {
          isAssigned: true,
          auditorName: auditor?.name || assignment.auditor_name || 'Unknown',
          auditorId: assignment.auditor_id,
          assignmentId: assignment.id
        };
      }
    }
    return { isAssigned: false, auditorName: null, auditorId: null, assignmentId: null };
  };

  const handleAssignUsers = async () => {
    if (!selectedAuditorForAssign || selectedUsersForAssignment.length === 0) {
      toast.error('Please select an auditor and at least one user');
      return;
    }

    setAssigningLoading(true);
    try {
      const existingAssignment = assignments.find(a => a.auditor_id === selectedAuditorForAssign);
      
      if (existingAssignment) {
        const currentUserIds = existingAssignment.assigned_user_ids || [];
        const newUserIds = [...new Set([...currentUserIds, ...selectedUsersForAssignment])];
        await api.put(`/qa-tasks/assignments/${existingAssignment.id}`, { assigned_user_ids: newUserIds });
      } else {
        await api.post('/qa-tasks/assignments', {
          tenant_id: tenant?.id || tenant?.tenant_id || '',
          auditor_id: selectedAuditorForAssign,
          assigned_user_ids: selectedUsersForAssignment,
          assigned_by: currentUser?.id || currentUser?.user_id || '',
          status: 'active'
        });
      }
      
      toast.success(`${selectedUsersForAssignment.length} user(s) assigned successfully`);
      setShowAssignDialog(false);
      setSelectedUsersForAssignment([]);
      setSelectedAuditorForAssign('');
      fetchAssignmentsData();
    } catch (error) {
      console.error('Assignment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign users');
    } finally {
      setAssigningLoading(false);
    }
  };

  const handleUnassignUsers = async (userIds) => {
    const toUnassign = Array.isArray(userIds) ? userIds : [userIds];
    
    try {
      const assignmentUpdates = new Map();
      
      toUnassign.forEach(userId => {
        const info = getUserAssignmentInfo(userId);
        if (info.assignmentId) {
          const assignment = assignments.find(a => a.id === info.assignmentId);
          if (assignment) {
            if (!assignmentUpdates.has(assignment.id)) {
              assignmentUpdates.set(assignment.id, {
                currentUsers: [...(assignment.assigned_user_ids || [])],
                toRemove: []
              });
            }
            assignmentUpdates.get(assignment.id).toRemove.push(userId);
          }
        }
      });

      for (const [assignmentId, data] of assignmentUpdates) {
        const newUserIds = data.currentUsers.filter(id => !data.toRemove.includes(id));
        if (newUserIds.length === 0) {
          await api.delete(`/qa-tasks/assignments/${assignmentId}`);
        } else {
          await api.put(`/qa-tasks/assignments/${assignmentId}`, { assigned_user_ids: newUserIds });
        }
      }

      toast.success(`${toUnassign.length} user(s) unassigned`);
      setSelectedUsersForAssignment([]);
      fetchAssignmentsData();
    } catch (error) {
      toast.error('Failed to unassign users');
    }
  };

  const handleReassignUsers = async () => {
    if (!selectedAuditorForAssign || selectedUsersForAssignment.length === 0) {
      toast.error('Please select an auditor');
      return;
    }

    setAssigningLoading(true);
    try {
      await handleUnassignUsers(selectedUsersForAssignment);
      
      const existingAssignment = assignments.find(a => a.auditor_id === selectedAuditorForAssign);
      if (existingAssignment) {
        const currentUserIds = existingAssignment.assigned_user_ids || [];
        const newUserIds = [...new Set([...currentUserIds, ...selectedUsersForAssignment])];
        await api.put(`/qa-tasks/assignments/${existingAssignment.id}`, { assigned_user_ids: newUserIds });
      } else {
        await api.post('/qa-tasks/assignments', {
          tenant_id: tenant?.id || tenant?.tenant_id || '',
          auditor_id: selectedAuditorForAssign,
          assigned_user_ids: selectedUsersForAssignment,
          assigned_by: currentUser?.id || currentUser?.user_id || '',
          status: 'active'
        });
      }
      
      toast.success(`${selectedUsersForAssignment.length} user(s) reassigned`);
      setShowReassignDialog(false);
      setSelectedUsersForAssignment([]);
      setSelectedAuditorForAssign('');
      fetchAssignmentsData();
    } catch (error) {
      console.error('Reassignment error:', error);
      toast.error('Failed to reassign users');
    } finally {
      setAssigningLoading(false);
    }
  };

  const getFilteredUsersForAssignment = () => {
    let result = users.map(u => ({
      ...u,
      ...getUserAssignmentInfo(u.id)
    }));

    // Tab filter
    if (assignmentsTab === 'assigned') result = result.filter(u => u.isAssigned);
    if (assignmentsTab === 'unassigned') result = result.filter(u => !u.isAssigned);

    // Auditor filter
    if (filterAuditor !== 'all') result = result.filter(u => u.auditorId === filterAuditor);

    // Search filter
    if (assignmentSearchQuery) {
      const query = assignmentSearchQuery.toLowerCase();
      result = result.filter(u => 
        u.name?.toLowerCase().includes(query) || 
        u.email?.toLowerCase().includes(query) ||
        u.designation?.toLowerCase().includes(query)
      );
    }

    return result;
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter and sort users
  useEffect(() => {
    let result = [...users];
    
    // Enhanced search filter - searches across multiple fields
    if (searchQuery) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(user => {
        // Basic fields
        const matchesBasic = 
          user.name?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query) ||
          user.designation?.toLowerCase().includes(query) ||
          user.employee_id?.toLowerCase().includes(query);
        
        // Phone numbers (primary and additional)
        const matchesPhone = 
          user.phone_number?.includes(query) ||
          user.phone_number?.replace(/-/g, '').includes(query.replace(/-/g, '')) ||
          user.phone_numbers?.some(p => p?.includes(query) || p?.replace(/-/g, '').includes(query.replace(/-/g, '')));
        
        // Extensions (primary and additional)
        const matchesExtension = 
          user.extension?.includes(query) ||
          user.extensions?.some(e => e?.includes(query));
        
        // Location - check both location string and location_id lookup
        const locationName = locations.find(l => l.id === user.location_id)?.name?.toLowerCase() || '';
        const matchesLocation = 
          user.location?.toLowerCase().includes(query) ||
          locationName.includes(query);
        
        return matchesBasic || matchesPhone || matchesExtension || matchesLocation;
      });
    }
    
    // Department filter
    if (filterDepartment !== 'all') {
      result = result.filter(user => user.department_id === filterDepartment);
    }
    
    // Role filter
    if (filterRole !== 'all') {
      result = result.filter(user => user.role_id === filterRole);
    }
    
    // Status filter
    if (filterStatus !== 'all') {
      const isActive = filterStatus === 'active';
      result = result.filter(user => (user.is_active !== false) === isActive);
    }
    
    // Sort
    result.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      
      if (sortField === 'department') {
        aVal = a.department?.name || '';
        bVal = b.department?.name || '';
      } else if (sortField === 'role') {
        aVal = a.role?.name || '';
        bVal = b.role?.name || '';
      }
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    setFilteredUsers(result);
  }, [users, searchQuery, filterDepartment, filterRole, filterStatus, sortField, sortOrder]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Fetch user stats when viewing user profile
  const handleViewUser = async (user) => {
    setViewingUser(user);
    setViewingUserStats(null);
    setLoadingStats(true);
    try {
      const { data } = await getUserStats(user.id);
      setViewingUserStats(data);
    } catch (error) {
      console.error('Failed to fetch user stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    return sortOrder === 'asc' ? 
      <ChevronUp className="h-4 w-4 ml-1" /> : 
      <ChevronDown className="h-4 w-4 ml-1" />;
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!userForm.name || !userForm.email || !userForm.phone_number || !userForm.role_id || !userForm.department_id) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await createUser(userForm);
      const newUserId = response.data?.id;
      
      // Upload avatar if selected
      if (avatarFile && newUserId) {
        await handleAvatarUpload(newUserId);
      }
      
      toast.success('User created successfully');
      setUserDialogOpen(false);
      resetUserForm();
      fetchData();
    } catch (error) {
      // Surface the real backend error so the admin knows what to fix
      const detail = error?.response?.data?.detail;
      let msg = 'Failed to create user';
      if (typeof detail === 'string' && detail) {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // FastAPI validation errors come as an array of {loc, msg, type}
        msg = detail.map(d => `${(d.loc || []).slice(-1)[0] || 'field'}: ${d.msg}`).join('; ');
      } else if (typeof detail === 'object' && detail) {
        msg = detail.message || JSON.stringify(detail);
      } else if (error?.message) {
        msg = error.message;
      }
      console.error('Create user failed:', error?.response?.data || error);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    
    setIsSubmitting(true);
    try {
      // Upload avatar FIRST if new file selected (so we have the URL before updating user)
      let avatarUrl = null;
      if (avatarFile) {
        try {
          console.log('Uploading avatar first for user:', editingUser.id);
          const avatarResponse = await uploadUserAvatar(editingUser.id, avatarFile);
          avatarUrl = avatarResponse?.data?.avatar_url;
          console.log('Avatar uploaded successfully:', avatarUrl);
          toast.success('Profile picture uploaded!');
          setAvatarFile(null);
        } catch (avatarError) {
          console.error('Avatar upload failed:', avatarError);
          const avatarErrorMsg = avatarError.response?.data?.detail || avatarError.response?.data?.message || avatarError.message || 'Unknown error';
          toast.error(`Failed to upload profile picture: ${typeof avatarErrorMsg === 'object' ? JSON.stringify(avatarErrorMsg) : avatarErrorMsg}`);
          // Continue with user update even if avatar fails
        }
      }
      
      // Check if it's a login user (from tenant_users collection)
      if (editingUser.source === 'tenant_users' || editingUser.is_login_user) {
        // Get department and location names from IDs for the API
        const deptName = departments.find(d => d.id === userForm.department_id)?.name || 
                         (typeof editingUser.department === 'object' ? editingUser.department?.name : editingUser.department) || '';
        const locName = locations.find(l => l.id === userForm.location_id)?.name || 
                        (typeof editingUser.location === 'object' ? editingUser.location?.name : editingUser.location) || '';
        const roleName = roles.find(r => r.id === userForm.role_id)?.name || 
                         (typeof editingUser.role === 'object' ? editingUser.role?.name : editingUser.role) || '';
        
        // Login users have different fields - include designation and role
        const loginUserData = {
          name: userForm.name,
          role: roleName,
          role_id: userForm.role_id || editingUser.role_id,
          is_admin: editingUser.is_admin,
          phone_number: userForm.phone_number,
          extension: userForm.extension,
          direct_number: userForm.phone_numbers?.[0] || '',
          department: deptName,
          department_id: userForm.department_id || editingUser.department_id,
          designation: userForm.designation || editingUser.designation,
          location: locName,
          location_id: userForm.location_id || editingUser.location_id,
          reports_to: userForm.reports_to || editingUser.reports_to,
          hierarchy_level: userForm.hierarchy_level ? parseInt(userForm.hierarchy_level) : (editingUser.hierarchy_level || null),
          avatar_url: avatarUrl || editingUser.avatar_url  // Include new avatar URL if uploaded
        };
        await updateLoginUser(editingUser.id, loginUserData);
      } else {
        const userData = {
          ...userForm,
          hierarchy_level: userForm.hierarchy_level ? parseInt(userForm.hierarchy_level) : null,
          avatar_url: avatarUrl || editingUser.avatar_url  // Include new avatar URL if uploaded
        };
        await updateUser(editingUser.id, userData);
      }
      
      toast.success('User updated successfully');
      setEditingUser(null);
      resetUserForm();
      fetchData();
    } catch (error) {
      console.error('Update user error:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error';
      toast.error('Failed to update user: ' + (typeof errorMessage === 'object' ? JSON.stringify(errorMessage) : errorMessage));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetUserForm = () => {
    setUserForm({ 
      name: '', email: '', phone_number: '', extension: '', employee_id: '',
      phone_numbers: ['', ''],
      extensions: ['', ''],
      role_id: '', department_id: '', designation: '', location_id: '',
      reports_to: '', hierarchy_level: ''
    });
    setShowAdditionalPhones(false);
    setShowAdditionalExtensions(false);
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  // Handler for creating login-enabled users (Quality Auditors, Admins, etc.)
  const handleCreateLoginUser = async (e) => {
    e.preventDefault();
    if (!loginUserForm.name || !loginUserForm.email || !loginUserForm.password) {
      toast.error('Please fill all required fields (name, email, password)');
      return;
    }
    
    if (loginUserForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsCreatingLoginUser(true);
    try {
      const response = await createLoginUser(loginUserForm);
      const { credentials, user } = response.data;
      
      setCreatedCredentials(credentials);
      toast.success(`Login user "${user.name}" created successfully!`);
      
      // Optionally refresh the user list
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create login user';
      toast.error(message);
    } finally {
      setIsCreatingLoginUser(false);
    }
  };

  const resetLoginUserForm = () => {
    setLoginUserForm({
      name: '', email: '', password: '', role_id: 'quality_auditor',
      phone_number: '', extension: '', department: '',
      feature_access: {
        billing: false,
        master_delete: false,
        audio_settings: false,
        onedrive: false
      }
    });
    setCreatedCredentials(null);
  };

  // Handler for setting user password
  const openSetPasswordDialog = (user) => {
    setPasswordUser(user);
    setPasswordForm({ password: '', auto_generate: false });
    setGeneratedPassword(null);
    setPasswordDialogOpen(true);
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (!passwordUser) return;
    
    if (!passwordForm.auto_generate && passwordForm.password && passwordForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsSettingPassword(true);
    try {
      const response = await setUserPassword(passwordUser.id, {
        password: passwordForm.auto_generate ? null : passwordForm.password,
        auto_generate: passwordForm.auto_generate || !passwordForm.password
      });
      
      setGeneratedPassword(response.data.credentials);
      toast.success(`Password set for ${passwordUser.name}`);
      fetchData(); // Refresh to show password status
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to set password';
      toast.error(message);
    } finally {
      setIsSettingPassword(false);
    }
  };

  const handleRemovePassword = async (user) => {
    if (!confirm(`Remove password for ${user.name}? They will only be able to login via their unique link.`)) return;
    
    try {
      await removeUserPassword(user.id);
      toast.success(`Password removed for ${user.name}`);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to remove password';
      toast.error(message);
    }
  };

  const resetPasswordDialog = () => {
    setPasswordUser(null);
    setPasswordForm({ password: '', auto_generate: false });
    setGeneratedPassword(null);
  };

  const openEditDialog = (user) => {
    // Process additional phone numbers and extensions from arrays
    const additionalPhones = user.phone_numbers || [];
    const additionalExtensions = user.extensions || [];
    
    // Check if there are actual non-empty values
    const hasAdditionalPhones = additionalPhones.some(p => p && p.trim() !== '');
    const hasAdditionalExtensions = additionalExtensions.some(e => e && e.trim() !== '');
    
    setUserForm({
      name: user.name || '',
      email: user.email || '',
      phone_number: user.phone_number || '',
      extension: user.extension || '',
      employee_id: user.employee_id || '',
      phone_numbers: [additionalPhones[0] || '', additionalPhones[1] || ''],
      extensions: [additionalExtensions[0] || '', additionalExtensions[1] || ''],
      role_id: user.role_id || '',
      department_id: user.department_id || '',
      designation: user.designation || '',
      location_id: user.location_id || '',
      reports_to: user.reports_to || '',
      hierarchy_level: user.hierarchy_level?.toString() || ''
    });
    
    // Show additional phone/extension fields if user has actual values
    setShowAdditionalPhones(hasAdditionalPhones);
    setShowAdditionalExtensions(hasAdditionalExtensions);
    
    setAvatarFile(null);
    // Set preview from existing avatar
    setAvatarPreview(user.avatar_url ? getUserAvatarUrl(user.id) : null);
    setEditingUser(user);
  };

  const handleAvatarSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type - more lenient for mobile formats
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif'];
    const fileExtension = (file.name || '').toLowerCase().split('.').pop();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes('.' + fileExtension)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, WebP, or HEIC.');
      return;
    }
    
    // Validate file size (25MB max - server will compress)
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 25MB.');
      return;
    }
    
    // Show compression notice for large files
    if (file.size > 5 * 1024 * 1024) {
      toast.info('Large image will be automatically compressed...');
    }
    
    // Create preview URL and open cropper
    const previewUrl = URL.createObjectURL(file);
    setCropperImageSrc(previewUrl);
    setShowCropper(true);
    
    // Reset the file input so the same file can be selected again
    e.target.value = '';
  };

  const handleCropComplete = (croppedBlob) => {
    // Convert blob to File object for upload
    const croppedFile = new File([croppedBlob], 'profile-picture.jpg', { type: 'image/jpeg' });
    setAvatarFile(croppedFile);
    
    // Create preview from cropped blob
    const previewUrl = URL.createObjectURL(croppedBlob);
    setAvatarPreview(previewUrl);
    
    // Cleanup cropper state
    if (cropperImageSrc) {
      URL.revokeObjectURL(cropperImageSrc);
    }
    setCropperImageSrc(null);
    setShowCropper(false);
    
    toast.success('Image cropped successfully! Click Save to upload.');
  };

  const handleCropperClose = () => {
    if (cropperImageSrc) {
      URL.revokeObjectURL(cropperImageSrc);
    }
    setCropperImageSrc(null);
    setShowCropper(false);
  };

  const handleAvatarUpload = async (userId) => {
    if (!avatarFile) return;
    
    setIsUploadingAvatar(true);
    try {
      console.log('Uploading avatar for user:', userId, 'File:', avatarFile.name, 'Size:', avatarFile.size);
      const response = await uploadUserAvatar(userId, avatarFile);
      console.log('Avatar upload response:', response);
      toast.success('Profile picture uploaded successfully!');
      setAvatarFile(null);
      fetchData(); // Refresh to get updated avatar URL
    } catch (error) {
      console.error('Avatar upload error:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error';
      toast.error(`Failed to upload profile picture: ${typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg}`);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleAvatarDelete = async (userId) => {
    try {
      await deleteUserAvatar(userId);
      toast.success('Profile picture removed');
      setAvatarPreview(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to remove profile picture');
    }
  };

  const handleExportUsers = async () => {
    setIsExporting(true);
    try {
      const response = await exportUsers();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Users exported successfully');
    } catch (error) {
      toast.error('Failed to export users');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await getImportTemplate();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users_import_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImportUsers = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setIsImporting(true);
    setImportResult(null);
    try {
      const response = await importUsers(importFile);
      setImportResult(response.data);
      if (response.data.success > 0) {
        toast.success(`Successfully imported ${response.data.success} users`);
        fetchData();
      }
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} users failed to import`);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to import users';
      toast.error(errorMessage);
      console.error('Import error:', error);
    } finally {
      setIsImporting(false);
    }
  };

  const resetImportDialog = () => {
    setImportFile(null);
    setImportResult(null);
    if (importFileRef.current) {
      importFileRef.current.value = '';
    }
  };

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    if (!deptForm.name) {
      toast.error('Please enter department name');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await createDepartment(deptForm);
      toast.success('Department created successfully');
      setDeptDialogOpen(false);
      setDeptForm({ name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create department');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!roleForm.name) {
      toast.error('Please enter role name');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await createRole(roleForm);
      toast.success('Role created successfully');
      setRoleDialogOpen(false);
      setRoleForm({ name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create role');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    try {
      // Check if it's a login user (from tenant_users collection)
      if (user.source === 'tenant_users' || user.is_login_user) {
        await deleteLoginUser(user.id);
      } else {
        await deleteUser(user.id);
      }
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      console.error('Delete user error:', error);
      toast.error('Failed to delete user: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Deleted Users Functions
  const fetchDeletedUsers = async () => {
    setIsLoadingDeleted(true);
    try {
      const [usersRes, statsRes] = await Promise.all([
        getDeletedUsers(),
        getDeletedUsersArchivedStats()
      ]);
      setDeletedUsers(usersRes.data.users || []);
      setDeletedUsersStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch deleted users:', error);
      toast.error('Failed to load deleted users');
    } finally {
      setIsLoadingDeleted(false);
    }
  };

  const handleSoftDeleteUsers = async (userIds, reason) => {
    if (!reason || reason.trim().length < 3) {
      toast.error('Please provide a reason for deletion (at least 3 characters)');
      return;
    }
    setIsDeleting(true);
    try {
      const res = await softDeleteUsers(userIds, reason.trim());
      toast.success(res.data.message);
      setDeleteDialogOpen(false);
      setUsersToDelete([]);
      setDeleteReason('');
      fetchData();
      fetchDeletedUsers();
    } catch (error) {
      console.error('Soft delete error:', error);
      toast.error('Failed to archive users: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestoreUsers = async (userIds) => {
    if (!confirm(`Are you sure you want to restore ${userIds.length} user(s)?`)) return;
    try {
      const res = await restoreDeletedUsers(userIds);
      toast.success(res.data.message);
      setSelectedDeletedUsers([]);
      fetchData();
      fetchDeletedUsers();
    } catch (error) {
      console.error('Restore error:', error);
      toast.error('Failed to restore users: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handlePermanentDeleteUsers = async (userIds) => {
    if (!confirm(`Are you sure you want to PERMANENTLY delete ${userIds.length} user(s)? This action cannot be undone!`)) return;
    try {
      const res = await permanentDeleteUsers(userIds);
      toast.success(res.data.message);
      setSelectedDeletedUsers([]);
      fetchDeletedUsers();
    } catch (error) {
      console.error('Permanent delete error:', error);
      toast.error('Failed to permanently delete users: ' + (error.response?.data?.detail || error.message));
    }
  };

  const viewDeletedUserData = async (user) => {
    setViewingDeletedUser(user);
    try {
      const res = await getDeletedUserArchivedData(user.id);
      setDeletedUserArchivedData(res.data);
    } catch (error) {
      console.error('Failed to get archived data:', error);
      setDeletedUserArchivedData(null);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      const res = await toggleUserActive(user.id);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle user status');
    }
  };

  const handleRegenerateUrl = async (user) => {
    if (!confirm('This will invalidate the current access URL. Continue?')) return;
    try {
      await regenerateUserUrl(user.id);
      toast.success('Access URL regenerated');
      fetchData();
    } catch (error) {
      toast.error('Failed to regenerate URL');
    }
  };

  const viewActivityLog = async (user) => {
    setActivityLogUser(user);
    setLoadingActivity(true);
    try {
      const res = await getUserActivity(user.id, 50);
      setActivityLogs(res.data);
    } catch (error) {
      toast.error('Failed to load activity logs');
      setActivityLogs([]);
    } finally {
      setLoadingActivity(false);
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(`${window.location.origin}/user/${url}`);
    toast.success('URL copied to clipboard');
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users">
      <Tabs defaultValue="users" onValueChange={(v) => {
        if (v === 'deleted') fetchDeletedUsers();
        if (v === 'assignments') fetchAssignmentsData();
      }}>
        <div className="flex items-center justify-between mb-6">
          <TabsList>
            <TabsTrigger value="users" className="gap-2">
              <UsersIcon className="h-4 w-4" />
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="assignments" className="gap-2">
              <UserCheck className="h-4 w-4" />
              Assignments
            </TabsTrigger>
            <TabsTrigger value="departments" className="gap-2">
              <Building2 className="h-4 w-4" />
              Departments ({departments.length})
            </TabsTrigger>
            <TabsTrigger value="roles" className="gap-2">
              <UserCog className="h-4 w-4" />
              Roles ({roles.length})
            </TabsTrigger>
            <TabsTrigger value="deleted" className="gap-2 text-destructive hover:text-destructive">
              <Archive className="h-4 w-4" />
              Deleted ({deletedUsers.length})
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="users">
          <Card className="card-elevated">
            <CardHeader className="flex flex-col gap-4">
              <div className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">Team Members</CardTitle>
                <div className="flex items-center gap-2">
                  {/* Import/Export Buttons */}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleExportUsers}
                    disabled={isExporting || users.length === 0}
                  >
                    {isExporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                    Export
                  </Button>
                  <Dialog open={importDialogOpen} onOpenChange={(open) => { setImportDialogOpen(open); if (!open) resetImportDialog(); }}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Upload className="h-4 w-4 mr-2" />
                        Import
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Import Users</DialogTitle>
                        <DialogDescription>
                          Upload a CSV file to bulk import users. Download the template for the correct format.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <Button variant="outline" className="w-full" onClick={handleDownloadTemplate}>
                          <FileSpreadsheet className="h-4 w-4 mr-2" />
                          Download CSV Template
                        </Button>
                        <div className="space-y-2">
                          <Label>CSV File</Label>
                          <Input
                            ref={importFileRef}
                            type="file"
                            accept=".csv"
                            onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                            className="cursor-pointer"
                          />
                          {importFile && (
                            <p className="text-sm text-muted-foreground">Selected: {importFile.name}</p>
                          )}
                        </div>
                        
                        {importResult && (
                          <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2 text-green-600">
                                <CheckCircle className="h-4 w-4" />
                                <span className="font-medium">{importResult.success} imported</span>
                              </div>
                              {importResult.failed > 0 && (
                                <div className="flex items-center gap-2 text-red-600">
                                  <XCircle className="h-4 w-4" />
                                  <span className="font-medium">{importResult.failed} failed</span>
                                </div>
                              )}
                            </div>
                            {importResult.errors?.length > 0 && (
                              <div className="mt-2 max-h-32 overflow-y-auto">
                                <p className="text-sm font-medium text-foreground">Errors:</p>
                                {importResult.errors.map((error, i) => (
                                  <p key={i} className="text-xs text-red-600">{error}</p>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        
                        <Button 
                          className="w-full btn-primary" 
                          onClick={handleImportUsers}
                          disabled={!importFile || isImporting}
                        >
                          {isImporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                          Import Users
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  {/* Create Login User Button (for Quality Auditors, Admins with login access) */}
                  <Dialog open={loginUserDialogOpen} onOpenChange={(open) => {
                    setLoginUserDialogOpen(open);
                    if (!open) resetLoginUserForm();
                  }}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="border-purple-500 text-purple-600 hover:bg-purple-50" data-testid="create-login-user-btn">
                        <Shield className="h-4 w-4 mr-2" />
                        Create Login User
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <KeyRound className="h-5 w-5 text-purple-600" />
                          Create Login User
                        </DialogTitle>
                        <DialogDescription>
                          Create a user with login credentials (e.g., Quality Auditor). They can sign in to the platform.
                        </DialogDescription>
                      </DialogHeader>
                      
                      {createdCredentials ? (
                        <div className="space-y-4">
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <CheckCircle className="h-5 w-5 text-green-600" />
                              <span className="font-medium text-green-700">User Created Successfully!</span>
                            </div>
                            <p className="text-sm text-muted-foreground mb-4">Share these credentials with the user:</p>
                            
                            <div className="space-y-2 bg-card rounded p-3 border">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Email:</span>
                                <code className="text-sm font-mono bg-muted px-2 py-1 rounded">{createdCredentials.email}</code>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Password:</span>
                                <code className="text-sm font-mono bg-muted px-2 py-1 rounded">{createdCredentials.password}</code>
                              </div>
                            </div>
                            
                            <Button
                              className="w-full mt-4"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(`Email: ${createdCredentials.email}\nPassword: ${createdCredentials.password}`);
                                toast.success('Credentials copied to clipboard!');
                              }}
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Copy Credentials
                            </Button>
                          </div>
                          
                          <Button 
                            className="w-full" 
                            onClick={() => {
                              resetLoginUserForm();
                              setLoginUserDialogOpen(false);
                            }}
                          >
                            Done
                          </Button>
                        </div>
                      ) : (
                        <form onSubmit={handleCreateLoginUser} className="space-y-4">
                          <div className="space-y-2">
                            <Label>Name *</Label>
                            <Input
                              value={loginUserForm.name}
                              onChange={(e) => setLoginUserForm({...loginUserForm, name: e.target.value})}
                              placeholder="John Doe"
                              data-testid="login-user-name-input"
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Email *</Label>
                            <Input
                              type="email"
                              value={loginUserForm.email}
                              onChange={(e) => setLoginUserForm({...loginUserForm, email: e.target.value})}
                              placeholder="john@company.com"
                              data-testid="login-user-email-input"
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Password *</Label>
                            <Input
                              type="password"
                              value={loginUserForm.password}
                              onChange={(e) => setLoginUserForm({...loginUserForm, password: e.target.value})}
                              placeholder="At least 6 characters"
                              data-testid="login-user-password-input"
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Role *</Label>
                            <Select value={loginUserForm.role_id} onValueChange={(v) => setLoginUserForm({...loginUserForm, role_id: v})}>
                              <SelectTrigger data-testid="login-user-role-select">
                                <SelectValue placeholder="Select role" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="lead_quality_auditor">
                                  <div className="flex items-center gap-2">
                                    <Crown className="h-4 w-4 text-amber-500" />
                                    Lead Quality Auditor
                                  </div>
                                </SelectItem>
                                <SelectItem value="quality_auditor">
                                  <div className="flex items-center gap-2">
                                    <Shield className="h-4 w-4 text-purple-500" />
                                    Quality Auditor
                                  </div>
                                </SelectItem>
                                <SelectItem value="admin">
                                  <div className="flex items-center gap-2">
                                    <UserCog className="h-4 w-4 text-blue-500" />
                                    Admin
                                  </div>
                                </SelectItem>
                                <SelectItem value="user">
                                  <div className="flex items-center gap-2">
                                    <UsersIcon className="h-4 w-4 text-muted-foreground" />
                                    User
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            {(loginUserForm.role_id === 'quality_auditor' || loginUserForm.role_id === 'lead_quality_auditor') && (
                              <p className="text-xs text-purple-600 mt-1">
                                {loginUserForm.role_id === 'lead_quality_auditor' 
                                  ? 'Lead Quality Auditors can manage Quality Auditors and have full admin access.'
                                  : 'Quality Auditors have admin access but cannot access Master Delete, Azure Config, or Billing.'}
                              </p>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Phone Number</Label>
                              <Input
                                value={loginUserForm.phone_number}
                                onChange={(e) => setLoginUserForm({...loginUserForm, phone_number: e.target.value})}
                                placeholder="555-0101"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Extension</Label>
                              <Input
                                value={loginUserForm.extension}
                                onChange={(e) => setLoginUserForm({...loginUserForm, extension: e.target.value})}
                                placeholder="101"
                              />
                            </div>
                          </div>
                          
                          {/* Feature Access Section - Only show for non-admin roles */}
                          {!['admin', 'owner', 'quality_auditor', 'lead_quality_auditor'].includes(loginUserForm.role_id) && (
                            <div className="space-y-3 p-4 bg-muted/50 dark:bg-slate-800/50 rounded-lg border">
                              <Label className="flex items-center gap-2 text-sm font-medium">
                                <Shield className="h-4 w-4 text-purple-500" />
                                Feature Access Permissions
                              </Label>
                              <p className="text-xs text-muted-foreground mb-3">
                                Select which features this user can access. Admins and Quality Auditors have all access by default.
                              </p>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="flex items-center space-x-2">
                                  <Checkbox 
                                    id="access-billing"
                                    checked={loginUserForm.feature_access?.billing || false}
                                    onCheckedChange={(checked) => setLoginUserForm({
                                      ...loginUserForm, 
                                      feature_access: {...loginUserForm.feature_access, billing: checked}
                                    })}
                                  />
                                  <Label htmlFor="access-billing" className="text-sm font-normal cursor-pointer">
                                    Billing
                                  </Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Checkbox 
                                    id="access-master-delete"
                                    checked={loginUserForm.feature_access?.master_delete || false}
                                    onCheckedChange={(checked) => setLoginUserForm({
                                      ...loginUserForm, 
                                      feature_access: {...loginUserForm.feature_access, master_delete: checked}
                                    })}
                                  />
                                  <Label htmlFor="access-master-delete" className="text-sm font-normal cursor-pointer">
                                    Master Delete
                                  </Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Checkbox 
                                    id="access-audio-settings"
                                    checked={loginUserForm.feature_access?.audio_settings || false}
                                    onCheckedChange={(checked) => setLoginUserForm({
                                      ...loginUserForm, 
                                      feature_access: {...loginUserForm.feature_access, audio_settings: checked}
                                    })}
                                  />
                                  <Label htmlFor="access-audio-settings" className="text-sm font-normal cursor-pointer">
                                    Audio & Call Settings
                                  </Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Checkbox 
                                    id="access-onedrive"
                                    checked={loginUserForm.feature_access?.onedrive || false}
                                    onCheckedChange={(checked) => setLoginUserForm({
                                      ...loginUserForm, 
                                      feature_access: {...loginUserForm.feature_access, onedrive: checked}
                                    })}
                                  />
                                  <Label htmlFor="access-onedrive" className="text-sm font-normal cursor-pointer">
                                    OneDrive
                                  </Label>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Info for admin roles */}
                          {['admin', 'owner', 'quality_auditor', 'lead_quality_auditor'].includes(loginUserForm.role_id) && (
                            <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span className="text-sm text-green-700 dark:text-green-300">
                                This role has full access to all features (Billing, Master Delete, Audio Settings, OneDrive)
                              </span>
                            </div>
                          )}
                          
                          <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" disabled={isCreatingLoginUser} data-testid="submit-login-user-btn">
                            {isCreatingLoginUser ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />}
                            Create Login User
                          </Button>
                        </form>
                      )}
                    </DialogContent>
                  </Dialog>
                  
                  {/* Add User Button */}
                  <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="btn-primary" data-testid="add-user-btn" onClick={() => { resetUserForm(); setEditingUser(null); }}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add User
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Add New User</DialogTitle>
                        <DialogDescription>
                          Create a new team member and generate their unique access URL.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateUser} className="space-y-4">
                        {/* Profile Picture Upload */}
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className="h-20 w-20 rounded-full bg-muted border-2 border-dashed border-border flex items-center justify-center overflow-hidden">
                              {avatarPreview ? (
                                <img src={avatarPreview} alt="Preview" className="h-full w-full object-cover" />
                              ) : (
                                <Camera className="h-8 w-8 text-muted-foreground" />
                              )}
                            </div>
                            {avatarPreview && (
                              <button
                                type="button"
                                onClick={() => {
                                  setAvatarFile(null);
                                  setAvatarPreview(null);
                                }}
                                className="absolute -top-1 -right-1 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                          <div className="flex-1">
                            <Label className="text-sm font-medium">Profile Picture</Label>
                            <p className="text-xs text-muted-foreground mb-2">Optional. JPEG, PNG, GIF, WebP, or HEIC (max 25MB, auto-compressed)</p>
                            <input
                              type="file"
                              ref={avatarFileRef}
                              accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif"
                              onChange={handleAvatarSelect}
                              className="hidden"
                            />
                            <Button 
                              type="button" 
                              variant="outline" 
                              size="sm"
                              onClick={() => avatarFileRef.current?.click()}
                            >
                              <ImagePlus className="h-4 w-4 mr-2" />
                              {avatarPreview ? 'Change Photo' : 'Upload Photo'}
                            </Button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Name *</Label>
                            <Input
                              value={userForm.name}
                              onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                              placeholder="John Doe"
                              data-testid="user-name-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Email *</Label>
                            <Input
                              type="email"
                              value={userForm.email}
                              onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                              placeholder="john@example.com"
                              data-testid="user-email-input"
                            />
                          </div>
                        </div>
                        
                        {/* Phone Numbers Section */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Label>Phone Number *</Label>
                            {!showAdditionalPhones && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs text-blue-600 hover:text-blue-700"
                                onClick={() => setShowAdditionalPhones(true)}
                              >
                                <Plus className="h-3 w-3 mr-1" /> Add more
                              </Button>
                            )}
                          </div>
                          <Input
                            value={userForm.phone_number}
                            onChange={(e) => setUserForm({...userForm, phone_number: e.target.value})}
                            placeholder="Primary phone number"
                            data-testid="user-phone-input"
                          />
                          {showAdditionalPhones && (
                            <div className="space-y-2 pl-4 border-l-2 border-blue-200">
                              <Input
                                value={userForm.phone_numbers?.[0] || ''}
                                onChange={(e) => setUserForm({
                                  ...userForm, 
                                  phone_numbers: [e.target.value, userForm.phone_numbers?.[1] || '']
                                })}
                                placeholder="Additional phone #2"
                                className="h-9"
                              />
                              <Input
                                value={userForm.phone_numbers?.[1] || ''}
                                onChange={(e) => setUserForm({
                                  ...userForm, 
                                  phone_numbers: [userForm.phone_numbers?.[0] || '', e.target.value]
                                })}
                                placeholder="Additional phone #3"
                                className="h-9"
                              />
                              <p className="text-xs text-muted-foreground">
                                All phone numbers will be used for call matching
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {/* Extensions Section */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Label>Extension</Label>
                            {!showAdditionalExtensions && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs text-blue-600 hover:text-blue-700"
                                onClick={() => setShowAdditionalExtensions(true)}
                              >
                                <Plus className="h-3 w-3 mr-1" /> Add more
                              </Button>
                            )}
                          </div>
                          <Input
                            value={userForm.extension}
                            onChange={(e) => setUserForm({...userForm, extension: e.target.value})}
                            placeholder="Primary extension (e.g., 101)"
                          />
                          {showAdditionalExtensions && (
                            <div className="space-y-2 pl-4 border-l-2 border-purple-200">
                              <Input
                                value={userForm.extensions?.[0] || ''}
                                onChange={(e) => setUserForm({
                                  ...userForm, 
                                  extensions: [e.target.value, userForm.extensions?.[1] || '']
                                })}
                                placeholder="Additional extension #2"
                                className="h-9"
                              />
                              <Input
                                value={userForm.extensions?.[1] || ''}
                                onChange={(e) => setUserForm({
                                  ...userForm, 
                                  extensions: [userForm.extensions?.[0] || '', e.target.value]
                                })}
                                placeholder="Additional extension #3"
                                className="h-9"
                              />
                              <p className="text-xs text-muted-foreground">
                                All extensions will be used for call matching
                              </p>
                            </div>
                          )}
                        </div>
                        
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Employee ID</Label>
                          <Input
                            value={userForm.employee_id}
                            onChange={(e) => setUserForm({...userForm, employee_id: e.target.value})}
                            placeholder="EMP001"
                            data-testid="user-employee-id-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Designation</Label>
                          <Input
                            value={userForm.designation}
                            onChange={(e) => setUserForm({...userForm, designation: e.target.value})}
                            placeholder="Senior Recruiter"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Department *</Label>
                          <Select value={userForm.department_id} onValueChange={(v) => setUserForm({...userForm, department_id: v})}>
                            <SelectTrigger data-testid="user-dept-select">
                              <SelectValue placeholder="Select department" />
                            </SelectTrigger>
                            <SelectContent>
                              {departments.map((dept) => (
                                <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Role *</Label>
                          <Select value={userForm.role_id} onValueChange={(v) => setUserForm({...userForm, role_id: v})}>
                            <SelectTrigger data-testid="user-role-select">
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                            <SelectContent>
                              {roles.map((role) => (
                                <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            Location
                          </Label>
                          <Select value={userForm.location_id || 'none'} onValueChange={(v) => setUserForm({...userForm, location_id: v === 'none' ? '' : v})}>
                            <SelectTrigger data-testid="user-location-select">
                              <SelectValue placeholder="Select location" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No Location</SelectItem>
                              {locations.map((loc) => (
                                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <UsersIcon className="h-4 w-4" />
                            Reports To
                          </Label>
                          <Popover open={reportsToOpen} onOpenChange={setReportsToOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={reportsToOpen}
                                className="w-full justify-between font-normal"
                              >
                                {userForm.reports_to
                                  ? users.find(u => u.id === userForm.reports_to)?.name || 'Select manager...'
                                  : 'No Manager (Top Level)'}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-full p-0" align="start">
                              <Command>
                                <CommandInput 
                                  placeholder="Search managers..." 
                                  value={reportsToSearch}
                                  onValueChange={setReportsToSearch}
                                />
                                <CommandList>
                                  <CommandEmpty>No manager found.</CommandEmpty>
                                  <CommandGroup>
                                    <CommandItem
                                      value="none"
                                      onSelect={() => {
                                        setUserForm({...userForm, reports_to: ''});
                                        setReportsToOpen(false);
                                      }}
                                    >
                                      <Check className={`mr-2 h-4 w-4 ${!userForm.reports_to ? 'opacity-100' : 'opacity-0'}`} />
                                      No Manager (Top Level)
                                    </CommandItem>
                                    {users
                                      .filter(u => u.id !== editingUser?.id)
                                      .filter(u => 
                                        !reportsToSearch || 
                                        u.name?.toLowerCase().includes(reportsToSearch.toLowerCase()) ||
                                        u.designation?.toLowerCase().includes(reportsToSearch.toLowerCase())
                                      )
                                      .map(u => (
                                        <CommandItem
                                          key={u.id}
                                          value={u.name}
                                          onSelect={() => {
                                            setUserForm({...userForm, reports_to: u.id});
                                            setReportsToOpen(false);
                                            setReportsToSearch('');
                                          }}
                                        >
                                          <Check className={`mr-2 h-4 w-4 ${userForm.reports_to === u.id ? 'opacity-100' : 'opacity-0'}`} />
                                          {u.name} - {u.designation || u.role?.name || 'User'}
                                        </CommandItem>
                                      ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Star className="h-4 w-4" />
                            Hierarchy Level
                          </Label>
                          <Select value={userForm.hierarchy_level} onValueChange={(v) => setUserForm({...userForm, hierarchy_level: v})}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select level" />
                            </SelectTrigger>
                            <SelectContent>
                              {hierarchyLevels.map(level => (
                                <SelectItem key={level.level} value={level.level.toString()}>
                                  {level.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Button type="submit" className="w-full btn-primary" disabled={isSubmitting} data-testid="create-user-btn">
                        {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Create User
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
              
            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, phone, designation, or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterRole} onValueChange={setFilterRole}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    {roles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="cursor-pointer" onClick={() => handleSort('name')}>
                      <div className="flex items-center">User {renderSortIcon('name')}</div>
                    </TableHead>
                    <TableHead className="w-[100px] text-center">Insights</TableHead>
                    <TableHead className="cursor-pointer" onClick={() => handleSort('phone_number')}>
                      <div className="flex items-center">Phone {renderSortIcon('phone_number')}</div>
                    </TableHead>
                    <TableHead className="cursor-pointer" onClick={() => handleSort('employee_id')}>
                      <div className="flex items-center">Emp ID {renderSortIcon('employee_id')}</div>
                    </TableHead>
                    <TableHead className="cursor-pointer" onClick={() => handleSort('reports_to')}>
                      <div className="flex items-center">Reports To {renderSortIcon('reports_to')}</div>
                    </TableHead>
                    <TableHead className="cursor-pointer" onClick={() => handleSort('location')}>
                      <div className="flex items-center">Location {renderSortIcon('location')}</div>
                    </TableHead>
                    <TableHead className="cursor-pointer" onClick={() => handleSort('department')}>
                      <div className="flex items-center">Department {renderSortIcon('department')}</div>
                    </TableHead>
                    <TableHead className="cursor-pointer" onClick={() => handleSort('role')}>
                      <div className="flex items-center">Role {renderSortIcon('role')}</div>
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Access URL</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id} className={user.is_active === false ? 'opacity-60' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={getAvatarDisplayUrl(user)} />
                            <AvatarFallback className="bg-muted">{user.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <button 
                              onClick={() => {
                                if (user.unique_url) {
                                  // Open user's profile directly in a new tab
                                  window.open(`/user/${user.unique_url}/`, '_blank');
                                } else {
                                  // Fallback for users without unique_url
                                  navigate(`/dashboard/user-session?userId=${user.id}`);
                                }
                              }}
                              className="font-medium text-left hover:text-blue-600 hover:underline cursor-pointer transition-colors"
                              data-testid={`user-name-link-${user.id}`}
                              title={user.unique_url ? "Open user profile" : "View user session"}
                            >
                              {user.name}
                            </button>
                            <p className="text-xs text-muted-foreground">{user.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setInsightsUser(user)}
                          className="h-8 w-8 p-0 hover:bg-blue-100 hover:text-blue-600"
                          data-testid={`insights-btn-${user.id}`}
                          title="View Insights"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {bulkInsights[user.id] && (
                          <div className="text-xs mt-1">
                            <UserInsightsBadge userId={user.id} insights={bulkInsights} />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {user.phone_number}
                        {user.extension && <span className="text-muted-foreground"> x{user.extension}</span>}
                      </TableCell>
                      <TableCell>
                        {user.employee_id ? (
                          <span className="text-sm font-mono bg-muted dark:bg-slate-800 px-2 py-0.5 rounded">{user.employee_id}</span>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.reports_to ? (
                          <span className="text-sm flex items-center gap-1">
                            <UsersIcon className="h-3 w-3 text-muted-foreground" />
                            {users.find(u => u.id === user.reports_to)?.name || 'Unknown'}
                          </span>
                        ) : (
                          <Badge variant="outline" className="text-xs">Top Level</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.location_id || user.location ? (
                          <span className="text-sm flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {locations.find(l => l.id === user.location_id)?.name || user.location || '-'}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{user.department?.name}</Badge>
                      </TableCell>
                      <TableCell>{user.role?.name}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant={user.is_active !== false ? "default" : "secondary"}>
                            {user.is_active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                          {user.has_password && (
                            <Badge variant="outline" className="text-xs bg-green-50 border-green-300 text-green-700">
                              <Lock className="h-2.5 w-2.5 mr-1" />
                              Has Password
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyUrl(user.unique_url)}
                          className="gap-2 font-mono text-xs"
                        >
                          <Copy className="h-3 w-3" />
                          {user.unique_url?.slice(0, 8)}...
                        </Button>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewUser(user)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Profile
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditDialog(user)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => openSetPasswordDialog(user)} data-testid={`set-password-${user.id}`}>
                              <Key className="h-4 w-4 mr-2" />
                              {user.has_password ? 'Reset Password' : 'Set Password'}
                            </DropdownMenuItem>
                            {user.has_password && (
                              <DropdownMenuItem onClick={() => handleRemovePassword(user)} className="text-orange-600">
                                <LockOpen className="h-4 w-4 mr-2" />
                                Remove Password
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleToggleActive(user)}>
                              <Power className="h-4 w-4 mr-2" />
                              {user.is_active !== false ? 'Deactivate' : 'Activate'} Link
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleRegenerateUrl(user)}>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Regenerate URL
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => viewActivityLog(user)}>
                              <History className="h-4 w-4 mr-2" />
                              Activity Log
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`view-coaching-report-${user.id}`}
                              onClick={() => {
                                setCoachingReportUser(user);
                                setCoachingReportOpen(true);
                              }}
                            >
                              <Sparkles className="h-4 w-4 mr-2 text-violet-600" />
                              View Coaching Report
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`workspace-access-${user.id}`}
                              onClick={() => {
                                setWorkspaceAccessUser(user);
                                setWorkspaceAccessOpen(true);
                              }}
                            >
                              <Building2 className="h-4 w-4 mr-2 text-orange-600" />
                              Workspace access
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => {
                                setUsersToDelete([user.id]);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-amber-600"
                            >
                              <Archive className="h-4 w-4 mr-2" />
                              Archive User
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDeleteUser(user)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Permanently
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredUsers.length === 0 && (
                <p className="text-center text-muted-foreground py-12">
                  {users.length === 0 ? "No users yet. Click \"Add User\" to create one." : "No users match your filters."}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* =============== ASSIGNMENTS TAB =============== */}
        <TabsContent value="assignments">
          <div className="space-y-4">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Total Users</p>
                      <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{assignmentStats.total}</p>
                    </div>
                    <UsersIcon className="h-8 w-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 dark:text-green-400 font-medium">Assigned</p>
                      <p className="text-2xl font-bold text-green-700 dark:text-green-300">{assignmentStats.assigned}</p>
                    </div>
                    <UserCheck className="h-8 w-8 text-green-500" />
                  </div>
                  <p className="text-xs text-green-600 mt-1">
                    {assignmentStats.total > 0 ? Math.round((assignmentStats.assigned / assignmentStats.total) * 100) : 0}% of total
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">Unassigned</p>
                      <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{assignmentStats.unassigned}</p>
                    </div>
                    <UserX className="h-8 w-8 text-amber-500" />
                  </div>
                  <p className="text-xs text-amber-600 mt-1">
                    {assignmentStats.total > 0 ? Math.round((assignmentStats.unassigned / assignmentStats.total) * 100) : 0}% need assignment
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Main Card */}
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle>User-Auditor Assignments</CardTitle>
                    <CardDescription>Assign users to quality auditors for call review</CardDescription>
                  </div>
                  {selectedUsersForAssignment.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{selectedUsersForAssignment.length} selected</Badge>
                      <Button size="sm" onClick={() => setShowAssignDialog(true)}>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Assign
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setShowReassignDialog(true)}>
                        <ArrowRight className="h-4 w-4 mr-2" />
                        Reassign
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleUnassignUsers(selectedUsersForAssignment.filter(id => getUserAssignmentInfo(id).isAssigned))}
                      >
                        <UserX className="h-4 w-4 mr-2" />
                        Unassign
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Filters */}
                <div className="flex flex-col md:flex-row gap-4 mt-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name, email, or designation..."
                      value={assignmentSearchQuery}
                      onChange={(e) => setAssignmentSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={filterAuditor} onValueChange={setFilterAuditor}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Filter by auditor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Auditors</SelectItem>
                      {auditors.map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              
              <CardContent>
                {/* Sub-tabs */}
                <Tabs value={assignmentsTab} onValueChange={setAssignmentsTab} className="mb-4">
                  <TabsList>
                    <TabsTrigger value="all">All ({assignmentStats.total})</TabsTrigger>
                    <TabsTrigger value="assigned">Assigned ({assignmentStats.assigned})</TabsTrigger>
                    <TabsTrigger value="unassigned">Unassigned ({assignmentStats.unassigned})</TabsTrigger>
                  </TabsList>
                </Tabs>

                {assignmentsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">
                            <Checkbox
                              checked={selectedUsersForAssignment.length === getFilteredUsersForAssignment().length && getFilteredUsersForAssignment().length > 0}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedUsersForAssignment(getFilteredUsersForAssignment().map(u => u.id));
                                } else {
                                  setSelectedUsersForAssignment([]);
                                }
                              }}
                            />
                          </TableHead>
                          <TableHead>User</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Designation</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Assigned To</TableHead>
                          <TableHead className="w-12">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getFilteredUsersForAssignment().length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8">
                              <UsersIcon className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
                              <p className="text-muted-foreground">No users found</p>
                            </TableCell>
                          </TableRow>
                        ) : (
                          getFilteredUsersForAssignment().map(userItem => (
                            <TableRow key={userItem.id} className={selectedUsersForAssignment.includes(userItem.id) ? "bg-muted/50" : ""}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedUsersForAssignment.includes(userItem.id)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSelectedUsersForAssignment(prev => [...prev, userItem.id]);
                                    } else {
                                      setSelectedUsersForAssignment(prev => prev.filter(id => id !== userItem.id));
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <Avatar className="h-8 w-8">
                                    <AvatarImage src={userItem.avatar_url} />
                                    <AvatarFallback className="text-xs">
                                      {userItem.name ? userItem.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?'}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="font-medium">{userItem.name || 'Unknown'}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-muted-foreground">{userItem.email}</TableCell>
                              <TableCell>{userItem.designation || '-'}</TableCell>
                              <TableCell>
                                {userItem.isAssigned ? (
                                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Assigned
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary" className="text-amber-700 dark:text-amber-300">
                                    <XCircle className="h-3 w-3 mr-1" />
                                    Unassigned
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>{userItem.auditorName || '-'}</TableCell>
                              <TableCell>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    {!userItem.isAssigned ? (
                                      <DropdownMenuItem onClick={() => {
                                        setSelectedUsersForAssignment([userItem.id]);
                                        setShowAssignDialog(true);
                                      }}>
                                        <UserPlus className="h-4 w-4 mr-2" />
                                        Assign to Auditor
                                      </DropdownMenuItem>
                                    ) : (
                                      <>
                                        <DropdownMenuItem onClick={() => {
                                          setSelectedUsersForAssignment([userItem.id]);
                                          setShowReassignDialog(true);
                                        }}>
                                          <ArrowRight className="h-4 w-4 mr-2" />
                                          Reassign
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleUnassignUsers(userItem.id)} className="text-destructive">
                                          <UserX className="h-4 w-4 mr-2" />
                                          Unassign
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Auditor Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Auditor Summary</CardTitle>
                <CardDescription>Users assigned to each auditor</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {auditors.map(auditor => {
                    const auditorAssignment = assignments.find(a => a.auditor_id === auditor.id);
                    const assignedCount = auditorAssignment?.assigned_user_ids?.length || 0;
                    return (
                      <Card key={auditor.id} className="bg-muted/30">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <Avatar className="h-10 w-10">
                              <AvatarImage src={auditor.avatar_url} />
                              <AvatarFallback>{auditor.name ? auditor.name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase() : '?'}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{auditor.name || 'Unnamed'}</p>
                              <p className="text-xs text-muted-foreground">{auditor.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Assigned Users</span>
                            <Badge variant={assignedCount > 0 ? "default" : "secondary"}>{assignedCount}</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  {auditors.length === 0 && (
                    <p className="text-muted-foreground col-span-full text-center py-4">
                      No auditors found. Create users with auditor role first.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Assign Dialog */}
          <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Assign Users to Auditor</DialogTitle>
                <DialogDescription>Select an auditor to assign {selectedUsersForAssignment.length} user(s)</DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <Label className="mb-2 block">Select Auditor</Label>
                <Select value={selectedAuditorForAssign} onValueChange={setSelectedAuditorForAssign}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an auditor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {auditors.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
                <Button onClick={handleAssignUsers} disabled={!selectedAuditorForAssign || assigningLoading}>
                  {assigningLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
                  Assign
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Reassign Dialog */}
          <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Reassign Users</DialogTitle>
                <DialogDescription>Select a new auditor for {selectedUsersForAssignment.length} user(s)</DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <Label className="mb-2 block">Select New Auditor</Label>
                <Select value={selectedAuditorForAssign} onValueChange={setSelectedAuditorForAssign}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an auditor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {auditors.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowReassignDialog(false)}>Cancel</Button>
                <Button onClick={handleReassignUsers} disabled={!selectedAuditorForAssign || assigningLoading}>
                  {assigningLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                  Reassign
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="departments">
          <Card className="card-elevated">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">Departments</CardTitle>
              <Dialog open={deptDialogOpen} onOpenChange={setDeptDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-dept-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Department
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Department</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateDepartment} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={deptForm.name}
                        onChange={(e) => setDeptForm({...deptForm, name: e.target.value})}
                        placeholder="US Recruitment"
                        data-testid="dept-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={deptForm.description}
                        onChange={(e) => setDeptForm({...deptForm, description: e.target.value})}
                        placeholder="Optional description"
                      />
                    </div>
                    <Button type="submit" className="w-full btn-primary" disabled={isSubmitting} data-testid="create-dept-btn">
                      {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Create Department
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {departments.map((dept) => (
                  <Card key={dept.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium">{dept.name}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{dept.description || 'No description'}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => deleteDepartment(dept.id).then(fetchData)}
                        className="text-muted-foreground hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
              {departments.length === 0 && (
                <p className="text-center text-muted-foreground py-12">No departments yet.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="roles">
          <Card className="card-elevated">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">Roles</CardTitle>
              <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-role-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Role
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Role</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateRole} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={roleForm.name}
                        onChange={(e) => setRoleForm({...roleForm, name: e.target.value})}
                        placeholder="Senior Recruiter"
                        data-testid="role-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={roleForm.description}
                        onChange={(e) => setRoleForm({...roleForm, description: e.target.value})}
                        placeholder="Optional description"
                      />
                    </div>
                    <Button type="submit" className="w-full btn-primary" disabled={isSubmitting} data-testid="create-role-btn">
                      {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Create Role
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {roles.map((role) => (
                  <Card key={role.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium">{role.name}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{role.description || 'No description'}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => deleteRole(role.id).then(fetchData)}
                        className="text-muted-foreground hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
              {roles.length === 0 && (
                <p className="text-center text-muted-foreground py-12">No roles yet.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Deleted Users Tab */}
        <TabsContent value="deleted">
          <Card className="card-elevated">
            <CardHeader className="flex flex-col gap-4">
              <div className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Archive className="h-5 w-5 text-destructive" />
                    Deleted Users
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Archived users and their related data. Restore or permanently delete.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {selectedDeletedUsers.length > 0 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestoreUsers(selectedDeletedUsers)}
                        className="gap-2"
                      >
                        <RotateCcw className="h-4 w-4" />
                        Restore ({selectedDeletedUsers.length})
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handlePermanentDeleteUsers(selectedDeletedUsers)}
                        className="gap-2"
                      >
                        <Trash2 className="h-4 w-4" />
                        Permanent Delete
                      </Button>
                    </>
                  )}
                  <Button variant="outline" size="sm" onClick={fetchDeletedUsers} disabled={isLoadingDeleted}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingDeleted ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>
              
              {/* Stats Summary */}
              {deletedUsersStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-destructive">{deletedUsersStats.deleted_users}</p>
                    <p className="text-xs text-muted-foreground">Deleted Users</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{deletedUsersStats.archived_calls}</p>
                    <p className="text-xs text-muted-foreground">Archived Calls</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{deletedUsersStats.archived_comm_sessions}</p>
                    <p className="text-xs text-muted-foreground">Coach Sessions</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-muted-foreground">{deletedUsersStats.total_archived_records}</p>
                    <p className="text-xs text-muted-foreground">Total Records</p>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {isLoadingDeleted ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : deletedUsers.length === 0 ? (
                <div className="text-center py-12">
                  <Archive className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No deleted users found.</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    When users are archived, they will appear here.
                  </p>
                </div>
              ) : (
                <div className="rounded-md border">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-3 text-left w-10">
                          <input
                            type="checkbox"
                            checked={selectedDeletedUsers.length === deletedUsers.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDeletedUsers(deletedUsers.map(u => u.id));
                              } else {
                                setSelectedDeletedUsers([]);
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                        </th>
                        <th className="p-3 text-left text-sm font-medium">User</th>
                        <th className="p-3 text-left text-sm font-medium">Deleted At</th>
                        <th className="p-3 text-left text-sm font-medium">Reason</th>
                        <th className="p-3 text-left text-sm font-medium">Archived Data</th>
                        <th className="p-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {deletedUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-muted/50">
                          <td className="p-3">
                            <input
                              type="checkbox"
                              checked={selectedDeletedUsers.includes(user.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedDeletedUsers([...selectedDeletedUsers, user.id]);
                                } else {
                                  setSelectedDeletedUsers(selectedDeletedUsers.filter(id => id !== user.id));
                                }
                              }}
                              className="rounded border-gray-300"
                            />
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="bg-destructive/10 text-destructive text-xs">
                                  {user.name?.slice(0, 2).toUpperCase() || 'U'}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium line-through text-muted-foreground">{user.name}</p>
                                <p className="text-xs text-muted-foreground">{user.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">
                            {user.deleted_at ? new Date(user.deleted_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="p-3 text-sm max-w-[200px] truncate" title={user.deletion_reason}>
                            {user.deletion_reason || '-'}
                          </td>
                          <td className="p-3">
                            {user.archived_related_data && (
                              <div className="flex flex-wrap gap-1">
                                {Object.entries(user.archived_related_data).slice(0, 3).map(([key, val]) => 
                                  val > 0 && (
                                    <Badge key={key} variant="secondary" className="text-xs">
                                      {key.replace(/_/g, ' ')}: {val}
                                    </Badge>
                                  )
                                )}
                              </div>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => viewDeletedUserData(user)}
                                title="View archived data"
                              >
                                <Database className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRestoreUsers([user.id])}
                                title="Restore user"
                                className="text-green-600 hover:text-green-700"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePermanentDeleteUsers([user.id])}
                                title="Permanently delete"
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View Deleted User Archived Data Dialog */}
      <Dialog open={!!viewingDeletedUser} onOpenChange={(open) => !open && setViewingDeletedUser(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Archived Data for {viewingDeletedUser?.name}
            </DialogTitle>
            <DialogDescription>
              Summary of all data archived when this user was deleted.
            </DialogDescription>
          </DialogHeader>
          {deletedUserArchivedData ? (
            <div className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Deleted on</p>
                <p className="font-medium">
                  {viewingDeletedUser?.deleted_at 
                    ? new Date(viewingDeletedUser.deleted_at).toLocaleString() 
                    : '-'}
                </p>
                <p className="text-sm text-muted-foreground mt-2">Reason</p>
                <p className="font-medium">{viewingDeletedUser?.deletion_reason || 'No reason provided'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(deletedUserArchivedData.archived_counts || {}).map(([key, val]) => (
                  <div key={key} className="flex items-center justify-between p-3 border rounded-lg">
                    <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                    <Badge variant={val > 0 ? 'default' : 'secondary'}>{val}</Badge>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg">
                <span className="font-medium">Total Archived Records</span>
                <span className="text-xl font-bold text-primary">
                  {deletedUserArchivedData.total_archived_records}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Soft Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Archive Users
            </DialogTitle>
            <DialogDescription>
              This will archive the selected user(s) and all their related data. You can restore them later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                {usersToDelete.length} user(s) will be archived
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                All calls, coaching records, assessments, and other data will be moved to archive.
              </p>
            </div>
            <div>
              <Label htmlFor="deleteReason">Reason for archival *</Label>
              <Input
                id="deleteReason"
                placeholder="Enter reason for archiving these users..."
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleSoftDeleteUsers(usersToDelete, deleteReason)}
              disabled={isDeleting || !deleteReason.trim()}
            >
              {isDeleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Archive Users
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user information.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4">
            {/* Profile Picture Upload */}
            <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
              <div className="relative">
                <div className="h-20 w-20 rounded-full bg-muted border-2 border-dashed border-border flex items-center justify-center overflow-hidden">
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="Preview" className="h-full w-full object-cover" />
                  ) : (
                    <Camera className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                {avatarPreview && (
                  <button
                    type="button"
                    onClick={() => {
                      if (editingUser?.id) {
                        handleAvatarDelete(editingUser.id);
                      } else {
                        setAvatarFile(null);
                        setAvatarPreview(null);
                      }
                    }}
                    className="absolute -top-1 -right-1 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
              <div className="flex-1">
                <Label className="text-sm font-medium">Profile Picture</Label>
                <p className="text-xs text-muted-foreground mb-2">Optional. JPEG, PNG, GIF, or WebP (max 30MB, auto-compressed)</p>
                <input
                  type="file"
                  ref={avatarFileRef}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleAvatarSelect}
                  className="hidden"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => avatarFileRef.current?.click()}
                >
                  <ImagePlus className="h-4 w-4 mr-2" />
                  {avatarPreview ? 'Change Photo' : 'Upload Photo'}
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={userForm.name}
                  onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone Number *</Label>
                <Input
                  value={userForm.phone_number}
                  onChange={(e) => setUserForm({...userForm, phone_number: e.target.value})}
                />
                {/* Additional Phone Numbers Toggle */}
                {!showAdditionalPhones && (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="text-xs p-0 h-auto"
                    onClick={() => setShowAdditionalPhones(true)}
                  >
                    + Add more phone numbers
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                <Label>Extension</Label>
                <Input
                  value={userForm.extension}
                  onChange={(e) => setUserForm({...userForm, extension: e.target.value})}
                />
                {/* Additional Extensions Toggle */}
                {!showAdditionalExtensions && (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="text-xs p-0 h-auto"
                    onClick={() => setShowAdditionalExtensions(true)}
                  >
                    + Add more extensions
                  </Button>
                )}
              </div>
            </div>
            {/* Additional Phone Numbers Fields */}
            {showAdditionalPhones && (
              <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-blue-200">
                <div className="space-y-2">
                  <Label className="text-sm">Phone Number 2</Label>
                  <Input
                    value={userForm.phone_numbers?.[0] || ''}
                    onChange={(e) => setUserForm({...userForm, phone_numbers: [e.target.value, userForm.phone_numbers?.[1] || '']})}
                    placeholder="Secondary phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Phone Number 3</Label>
                  <Input
                    value={userForm.phone_numbers?.[1] || ''}
                    onChange={(e) => setUserForm({...userForm, phone_numbers: [userForm.phone_numbers?.[0] || '', e.target.value]})}
                    placeholder="Tertiary phone"
                  />
                </div>
                <p className="col-span-2 text-xs text-muted-foreground">
                  All phone numbers will be used for call matching
                </p>
              </div>
            )}
            {/* Additional Extensions Fields */}
            {showAdditionalExtensions && (
              <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-purple-200">
                <div className="space-y-2">
                  <Label className="text-sm">Extension 2</Label>
                  <Input
                    value={userForm.extensions?.[0] || ''}
                    onChange={(e) => setUserForm({...userForm, extensions: [e.target.value, userForm.extensions?.[1] || '']})}
                    placeholder="Secondary extension"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Extension 3</Label>
                  <Input
                    value={userForm.extensions?.[1] || ''}
                    onChange={(e) => setUserForm({...userForm, extensions: [userForm.extensions?.[0] || '', e.target.value]})}
                    placeholder="Tertiary extension"
                  />
                </div>
                <p className="col-span-2 text-xs text-muted-foreground">
                  All extensions will be used for call matching
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employee ID</Label>
                <Input
                  value={userForm.employee_id}
                  onChange={(e) => setUserForm({...userForm, employee_id: e.target.value})}
                  placeholder="EMP001"
                  data-testid="edit-user-employee-id-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Designation
                </Label>
                <Input
                  value={userForm.designation}
                  onChange={(e) => setUserForm({...userForm, designation: e.target.value})}
                  placeholder="Senior Recruiter"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department *</Label>
                <Select value={userForm.department_id} onValueChange={(v) => setUserForm({...userForm, department_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select value={userForm.role_id} onValueChange={(v) => setUserForm({...userForm, role_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Location
                </Label>
                <Select value={userForm.location_id || 'none'} onValueChange={(v) => setUserForm({...userForm, location_id: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="edit-user-location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Location</SelectItem>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <UsersIcon className="h-4 w-4" />
                  Reports To
                </Label>
                <Popover open={reportsToOpen} onOpenChange={setReportsToOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={reportsToOpen}
                      className="w-full justify-between font-normal"
                    >
                      {userForm.reports_to
                        ? users.find(u => u.id === userForm.reports_to)?.name || 'Select manager...'
                        : 'No Manager (Top Level)'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput 
                        placeholder="Search managers..." 
                        value={reportsToSearch}
                        onValueChange={setReportsToSearch}
                      />
                      <CommandList>
                        <CommandEmpty>No manager found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="none"
                            onSelect={() => {
                              setUserForm({...userForm, reports_to: ''});
                              setReportsToOpen(false);
                            }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${!userForm.reports_to ? 'opacity-100' : 'opacity-0'}`} />
                            No Manager (Top Level)
                          </CommandItem>
                          {users
                            .filter(u => u.id !== editingUser?.id)
                            .filter(u => 
                              !reportsToSearch || 
                              u.name?.toLowerCase().includes(reportsToSearch.toLowerCase()) ||
                              u.designation?.toLowerCase().includes(reportsToSearch.toLowerCase())
                            )
                            .map(u => (
                              <CommandItem
                                key={u.id}
                                value={u.name}
                                onSelect={() => {
                                  setUserForm({...userForm, reports_to: u.id});
                                  setReportsToOpen(false);
                                  setReportsToSearch('');
                                }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${userForm.reports_to === u.id ? 'opacity-100' : 'opacity-0'}`} />
                                {u.name} - {u.designation || u.role?.name || 'User'}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Star className="h-4 w-4" />
                  Hierarchy Level
                </Label>
                <Select value={userForm.hierarchy_level || ''} onValueChange={(v) => setUserForm({...userForm, hierarchy_level: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    {hierarchyLevels.map(level => (
                      <SelectItem key={level.level} value={level.level.toString()}>
                        {level.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="w-full btn-primary" disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Update User
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* View User Dialog */}
      <Dialog open={!!viewingUser} onOpenChange={(open) => { if (!open) { setViewingUser(null); setViewingUserStats(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>User Profile</DialogTitle>
            <DialogDescription>User details and performance statistics</DialogDescription>
          </DialogHeader>
          {viewingUser && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={viewingUser.avatar_url} alt={viewingUser.name} />
                  <AvatarFallback className="bg-muted text-lg">
                    {viewingUser.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-lg">{viewingUser.name}</h3>
                  <p className="text-sm text-muted-foreground">{viewingUser.designation || viewingUser.role?.name || 'User'}</p>
                </div>
              </div>

              {/* Reporting Structure */}
              {viewingUser.reports_to && (
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Reports To</p>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
                        {users.find(u => u.id === viewingUser.reports_to)?.name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-sm">{users.find(u => u.id === viewingUser.reports_to)?.name || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground">{users.find(u => u.id === viewingUser.reports_to)?.designation || users.find(u => u.id === viewingUser.reports_to)?.role?.name || ''}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Direct Reports */}
              {users.filter(u => u.reports_to === viewingUser.id).length > 0 && (
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-2">Direct Reports ({users.filter(u => u.reports_to === viewingUser.id).length})</p>
                  <div className="flex flex-wrap gap-2">
                    {users.filter(u => u.reports_to === viewingUser.id).map(report => (
                      <div key={report.id} className="flex items-center gap-1 bg-card px-2 py-1 rounded text-sm">
                        <Avatar className="h-5 w-5">
                          <AvatarFallback className="bg-green-100 text-green-700 text-[10px]">
                            {report.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        {report.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact & Role Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{viewingUser.email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Phone</p>
                  <p className="font-medium">{viewingUser.phone_number}{viewingUser.extension && ` x${viewingUser.extension}`}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Department</p>
                  <p className="font-medium">{viewingUser.department?.name || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Role</p>
                  <p className="font-medium">{viewingUser.role?.name || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Location</p>
                  <p className="font-medium">{locations.find(l => l.id === viewingUser.location_id)?.name || viewingUser.location || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <Badge variant={viewingUser.is_active !== false ? "default" : "secondary"}>
                    {viewingUser.is_active !== false ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>

              {/* Performance Stats */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-sm flex items-center gap-2 mb-4">
                  <BarChart3 className="h-4 w-4" />
                  Performance Statistics
                </h4>
                {loadingStats ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : viewingUserStats ? (
                  <div className="space-y-4">
                    {/* Overview Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-muted/50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-600">{viewingUserStats.total_calls || 0}</p>
                        <p className="text-xs text-muted-foreground">Total Calls</p>
                      </div>
                      <div className="bg-muted/50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-emerald-600">{viewingUserStats.average_score?.toFixed(1) || '0.0'}</p>
                        <p className="text-xs text-muted-foreground">Avg Score</p>
                      </div>
                      <div className="bg-muted/50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-600">{viewingUserStats.processed_calls || 0}</p>
                        <p className="text-xs text-muted-foreground">Processed</p>
                      </div>
                    </div>

                    {/* Classifications */}
                    {viewingUserStats.classifications && (
                      <div className="bg-muted/50 p-3 rounded-lg">
                        <p className="text-xs font-medium text-muted-foreground mb-2">Call Classifications</p>
                        <div className="flex gap-2">
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            Best: {viewingUserStats.classifications.best || 0}
                          </Badge>
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            Good: {viewingUserStats.classifications.good || 0}
                          </Badge>
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            Improvement: {viewingUserStats.classifications.improvement || 0}
                          </Badge>
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                            High Alert: {viewingUserStats.classifications.high_alert || 0}
                          </Badge>
                        </div>
                      </div>
                    )}

                    {/* Score Breakdown */}
                    {viewingUserStats.score_breakdown && Object.keys(viewingUserStats.score_breakdown).length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">Score Breakdown</p>
                        {Object.entries(viewingUserStats.score_breakdown).slice(0, 5).map(([key, value]) => (
                          <div key={key} className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground w-32 truncate capitalize">
                              {key.replace(/_/g, ' ')}
                            </span>
                            <Progress 
                              value={value * 10} 
                              className={`flex-1 h-2 ${
                                value >= 8 ? 'bg-emerald-100' :
                                value >= 6 ? 'bg-blue-100' :
                                value >= 4 ? 'bg-amber-100' : 'bg-red-100'
                              }`}
                            />
                            <span className={`text-xs font-medium w-8 ${
                              value >= 8 ? 'text-emerald-600' :
                              value >= 6 ? 'text-blue-600' :
                              value >= 4 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {value?.toFixed(1)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground text-sm py-4">No performance data available</p>
                )}
              </div>

              {/* Access URL */}
              <div className="pt-2 border-t">
                <p className="text-muted-foreground text-sm">Access URL</p>
                <div className="flex items-center gap-2 mt-1">
                  <code className="bg-muted px-2 py-1 rounded text-xs flex-1 truncate">
                    {window.location.origin}/user/{viewingUser.unique_url}
                  </code>
                  <Button size="sm" variant="outline" onClick={() => copyUrl(viewingUser.unique_url)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <a href={`/user/${viewingUser.unique_url}`} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="default">
                      Open Session
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Activity Log Dialog */}
      <Dialog open={!!activityLogUser} onOpenChange={(open) => !open && setActivityLogUser(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Activity Log - {activityLogUser?.name}</DialogTitle>
            <DialogDescription>Recent user activity and sessions</DialogDescription>
          </DialogHeader>
          {loadingActivity ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : activityLogs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No activity logs found for this user.</p>
          ) : (
            <div className="space-y-3">
              {activityLogs.map((log) => (
                <div key={log.id} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="flex-shrink-0 w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                    <History className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm">{log.action}</p>
                      <span className="text-xs text-muted-foreground">
                        {new Date(log.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 space-y-1">
                      {log.ip_address && <p>IP: {log.ip_address}</p>}
                      {log.browser && log.os && <p>Browser: {log.browser} on {log.os}</p>}
                      {log.device && <p>Device: {log.device}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Set Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={(open) => {
        setPasswordDialogOpen(open);
        if (!open) resetPasswordDialog();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-blue-600" />
              {passwordUser?.has_password ? 'Reset Password' : 'Set Password'}
            </DialogTitle>
            <DialogDescription>
              {passwordUser?.has_password 
                ? `Update the login password for ${passwordUser?.name}. They can use this to sign in via the production login page.`
                : `Set a login password for ${passwordUser?.name}. This enables them to sign in via the production login page in addition to their unique link.`
              }
            </DialogDescription>
          </DialogHeader>
          
          {generatedPassword ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-700">Password Set Successfully!</span>
                </div>
                <p className="text-sm text-muted-foreground mb-4">Share these credentials with the user:</p>
                
                <div className="space-y-2 bg-card rounded p-3 border">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Email:</span>
                    <code className="text-sm font-mono bg-muted px-2 py-1 rounded">{generatedPassword.email}</code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Password:</span>
                    <code className="text-sm font-mono bg-muted px-2 py-1 rounded">{generatedPassword.password}</code>
                  </div>
                </div>
                
                <Button
                  className="w-full mt-4"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(`Email: ${generatedPassword.email}\nPassword: ${generatedPassword.password}`);
                    toast.success('Credentials copied to clipboard!');
                  }}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Credentials
                </Button>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> The user can now sign in at the main login page ({window.location.origin}/login) using their email and this password. Their unique access link still works too.
                </p>
              </div>
              
              <Button 
                className="w-full" 
                onClick={() => {
                  resetPasswordDialog();
                  setPasswordDialogOpen(false);
                }}
              >
                Done
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSetPassword} className="space-y-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={passwordUser?.avatar_url} />
                    <AvatarFallback className="bg-muted">{passwordUser?.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{passwordUser?.name}</p>
                    <p className="text-sm text-muted-foreground">{passwordUser?.email}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="auto-generate" className="text-sm">Auto-generate secure password</Label>
                </div>
                <Switch
                  id="auto-generate"
                  checked={passwordForm.auto_generate}
                  onCheckedChange={(checked) => setPasswordForm({...passwordForm, auto_generate: checked})}
                />
              </div>
              
              {!passwordForm.auto_generate && (
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={passwordForm.password}
                    onChange={(e) => setPasswordForm({...passwordForm, password: e.target.value})}
                    placeholder="Enter password (min 6 characters)"
                    data-testid="set-password-input"
                  />
                  <p className="text-xs text-muted-foreground">Leave empty to auto-generate a secure password</p>
                </div>
              )}
              
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isSettingPassword} data-testid="confirm-set-password">
                {isSettingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Key className="h-4 w-4 mr-2" />}
                {passwordUser?.has_password ? 'Reset Password' : 'Set Password'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
      
      {/* User Insights Popup */}
      <UserInsightsPopup
        userId={insightsUser?.id}
        userName={insightsUser?.name}
        isOpen={!!insightsUser}
        onClose={() => setInsightsUser(null)}
      />

      {/* Cumulative Coaching Report */}
      <CumulativeLearningReport
        userId={coachingReportUser?.id}
        userName={coachingReportUser?.name}
        open={coachingReportOpen}
        onOpenChange={(o) => { setCoachingReportOpen(o); if (!o) setCoachingReportUser(null); }}
        canRegenerate={true}
      />

      {/* Workspace Access (Multi-tenant) */}
      <WorkspaceAccessDialog
        user={workspaceAccessUser}
        open={workspaceAccessOpen}
        onOpenChange={(o) => { setWorkspaceAccessOpen(o); if (!o) setWorkspaceAccessUser(null); }}
      />

      {/* Image Cropper Dialog */}
      <ImageCropper
        open={showCropper}
        onClose={handleCropperClose}
        imageSrc={cropperImageSrc}
        onCropComplete={handleCropComplete}
        aspectRatio={1}
      />
    </div>
  );
}
